import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { Send, Headset, CheckCheck, X, Lock } from 'lucide-react';
import contactRequestService, {
  ContactMessage,
  ContactRequest,
  categoryLabel,
  statusLabel,
} from '../../services/contactRequestService';

const ACCENT = '#3fced1';

const formatTime = (ts?: number): string => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

interface Props {
  request: ContactRequest;
  isOpen: boolean;
  onClose: () => void;
  onRequestUpdated: (updated: ContactRequest) => void;
}

const ContactRequestDetailModal: React.FC<Props> = ({
  request,
  isOpen,
  onClose,
  onRequestUpdated,
}) => {
  const [current, setCurrent] = useState<ContactRequest>(request);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  const markAsRead = useCallback(async () => {
    const updated = await contactRequestService.markAsRead(request.id);
    if (updated) {
      setCurrent(updated);
      onRequestUpdated(updated);
    }
  }, [request.id, onRequestUpdated]);

  // Beim Öffnen: Request übernehmen, als gelesen markieren, Polling starten
  useEffect(() => {
    if (!isOpen) return;
    setCurrent(request);
    markAsRead();
    scrollToBottom();

    pollRef.current = setInterval(async () => {
      const updated = await contactRequestService.getRequestById(request.id);
      if (updated) {
        setCurrent((prev) => {
          const hadNew = updated.messages.length > prev.messages.length;
          if (hadNew) {
            scrollToBottom();
            contactRequestService.markAsRead(request.id);
          }
          return updated;
        });
        onRequestUpdated(updated);
      }
    }, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, request.id]);

  const handleSend = async () => {
    const content = message.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const updated = await contactRequestService.sendMessage(current.id, content);
      if (updated) {
        setCurrent(updated);
        onRequestUpdated(updated);
        setMessage('');
        scrollToBottom();
      }
    } finally {
      setSending(false);
    }
  };

  const isClosed = current.status === 'closed';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      size="2xl"
      scrollBehavior="inside"
      backdrop="blur"
      classNames={{ base: 'bg-zinc-900 border border-zinc-800' }}
    >
      <ModalContent className="h-[80vh]">
        {() => (
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 p-5" style={{ backgroundColor: ACCENT }}>
              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold text-black">{current.subject}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-black/15 px-2 py-0.5 text-xs font-semibold text-black">
                    {statusLabel(current.status)}
                  </span>
                  <span className="rounded-md bg-black/10 px-2 py-0.5 text-xs text-black/80">
                    {categoryLabel(current.category)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                aria-label="Schließen"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/15 text-black transition-colors hover:bg-black/25"
              >
                <X size={18} />
              </button>
            </div>

            {/* Nachrichten */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-zinc-950 p-5">
              {current.messages.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-500">Noch keine Nachrichten.</p>
              ) : (
                current.messages.map((m, i) => <MessageBubble key={m.id || i} message={m} />)
              )}
            </div>

            {/* Eingabe / geschlossen */}
            {isClosed ? (
              <div className="flex items-center justify-center gap-2 border-t border-zinc-800 bg-zinc-900 p-4 text-sm text-gray-500">
                <Lock size={15} /> Diese Anfrage ist geschlossen.
              </div>
            ) : (
              <div className="flex items-end gap-2 border-t border-zinc-800 bg-zinc-900 p-4">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  rows={1}
                  placeholder="Nachricht schreiben..."
                  className="max-h-32 min-h-[44px] flex-1 resize-none rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm text-white outline-none placeholder:text-gray-500 focus:border-[#3fced1]/60"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!message.trim() || sending}
                  aria-label="Senden"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-black transition-opacity disabled:opacity-40"
                  style={{ backgroundColor: ACCENT }}
                >
                  <Send size={18} />
                </button>
              </div>
            )}
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

const MessageBubble: React.FC<{ message: ContactMessage }> = ({ message }) => {
  const isMine = message.senderType === 'user';

  return (
    <div className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
      {!isMine && (
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: 'rgba(63,206,209,0.2)', color: ACCENT }}
        >
          <Headset size={15} />
        </span>
      )}
      <div
        className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 ${
          isMine ? 'rounded-br-sm text-black' : 'rounded-bl-sm bg-zinc-800 text-white'
        }`}
        style={isMine ? { backgroundColor: ACCENT } : undefined}
      >
        {!isMine && (
          <p className="mb-1 text-[11px] font-semibold text-gray-400">{message.senderName}</p>
        )}
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.content}</p>
        <div
          className={`mt-1 flex items-center gap-1 text-[11px] ${
            isMine ? 'text-black/60' : 'text-gray-500'
          }`}
        >
          {formatTime(message.timestamp)}
          {isMine && message.read && <CheckCheck size={13} />}
        </div>
      </div>
    </div>
  );
};

export default ContactRequestDetailModal;
